/**
 * Training Plan CSV helpers
 *
 * Client-side helpers for the Training Plan import page:
 * - the canonical column headers the importer expects,
 * - a downloadable CSV template (header row + one example row),
 * - RFC4180-style single-row encode/decode used to build the template and
 *   round-trip tested with fast-check.
 *
 * NOTE: the authoritative parser lives in the backend Lambda
 * (`training-plans-import.py`); these helpers only produce the template and are
 * intentionally minimal.
 */

/** Ordered columns the importer recognizes (matches the backend COLUMN_MAP). */
export const TRAINING_PLAN_CSV_HEADERS = [
  'Week',
  'Day',
  'Training Phase',
  'Periodization Focus',
  'Session Type',
  'Target RPE',
  'Prescribed Workout',
  'Weekly Skill Emphasis',
  'Coaches Note',
] as const;

/** A single illustrative example row (aligned to TRAINING_PLAN_CSV_HEADERS). */
export const TRAINING_PLAN_CSV_EXAMPLE_ROW = [
  '1',
  'Wed',
  'Base 1',
  'Aerobic Endurance',
  'Climbing intervals',
  'RPE 3-4',
  '60 min endurance ride, 3x8 min tempo',
  'Braking mechanics & basic modulation',
  'Keep cadence high; focus on smooth braking',
] as const;

/** Encode one row of fields to an RFC4180 CSV line (quote when needed). */
export function toCsvRow(values: readonly string[]): string {
  return values
    .map((v) => {
      const s = v ?? '';
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    })
    .join(',');
}

/** Decode a single RFC4180 CSV line into its fields (inverse of toCsvRow). */
export function parseCsvRow(line: string): string[] {
  const out: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      out.push(field);
      field = '';
    } else {
      field += c;
    }
  }
  out.push(field);
  return out;
}

/** Build the downloadable CSV template: header row + one example row. */
export function buildTrainingPlanCsvTemplate(): string {
  return `${toCsvRow(TRAINING_PLAN_CSV_HEADERS)}\n${toCsvRow(TRAINING_PLAN_CSV_EXAMPLE_ROW)}\n`;
}

/** Trigger a browser download of the given text as a CSV file. */
export function downloadCsv(filename: string, text: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
